import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from '../../assets/icons';
import { SupportNoteSource, SupportNoteTag } from '../../stores/types/supportnote.interface';
import { CheckBox, CustomDatePicker, Icon } from '../_ui';
import { DATE_RANGE } from '../_ui/date-picker/Custom-Date-Picker.component';
import { ALL_EVENT_TYPES, NOTE_TYPES } from './ChargerHistoryUtils';
import { ChargerHistoryFiltersProps } from './ChargerHistory.interface';

const DATE_PICKER_OPTIONS = [DATE_RANGE.LAST_WEEK, DATE_RANGE.LAST_MONTH, DATE_RANGE.CUSTOM_RANGE];

const ChargerHistoryFilters: FC<ChargerHistoryFiltersProps> = ({
  dateOption,
  selectedDate,
  onDateChange,
  selectedTags,
  onTagsChange,
}) => {
  const { t } = useTranslation();
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [isEventTypeOpen, setIsEventTypeOpen] = useState(false);
  const eventTypeRef = useRef<HTMLDivElement>(null);

  // Close event type dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (eventTypeRef.current && !eventTypeRef.current.contains(event.target as Node)) {
        setIsEventTypeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAllSelected = useMemo(
    () => ALL_EVENT_TYPES.every((tag) => selectedTags.includes(tag)),
    [selectedTags],
  );

  const someAllSelected = useMemo(
    () =>
      selectedTags.length > 0 &&
      ALL_EVENT_TYPES.some((tag) => selectedTags.includes(tag)) &&
      !isAllSelected,
    [selectedTags, isAllSelected],
  );

  const allNotesSelected = useMemo(
    () => NOTE_TYPES.every((tag) => selectedTags.includes(tag)),
    [selectedTags],
  );

  const someNotesSelected = useMemo(() => {
    const noteCount = NOTE_TYPES.filter((tag) => selectedTags.includes(tag)).length;
    return noteCount > 0 && noteCount < NOTE_TYPES.length;
  }, [selectedTags]);

  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      onTagsChange([]);
    } else {
      onTagsChange(ALL_EVENT_TYPES);
    }
  }, [isAllSelected, onTagsChange]);

  const handleToggleNotesAll = () => {
    if (allNotesSelected) {
      const remaining = selectedTags.filter((tag) => !NOTE_TYPES.includes(tag));
      onTagsChange(remaining);
    } else {
      onTagsChange(Array.from(new Set([...selectedTags, ...NOTE_TYPES])));
    }
  };

  const handleToggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter((t) => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const toggleEventType = () => {
    setIsEventTypeOpen((prev) => !prev);
  };

  const toggleNotesExpanded = () => {
    setIsNotesExpanded((prev) => !prev);
  };

  return (
    <div className='flex flex-row items-center gap-2 flex-wrap'>
      {/* Time filter - using CustomDatePicker like Overview page */}
      <CustomDatePicker
        optionList={DATE_PICKER_OPTIONS}
        selectedOption={dateOption}
        selectedDate={selectedDate}
        onChange={onDateChange}
        showHighligtHeader={false}
      />

      {/* Event type filter */}
      <div className='relative' ref={eventTypeRef}>
        <button
          className='flex items-center gap-1.5 px-3 py-2 bg-grey1 hover:bg-grey2 rounded text-sm font-inter font-medium text-grey6 border-0 cursor-pointer h-10'
          onClick={toggleEventType}
        >
          <span>{t('charger_history_event_type')}</span>
          <Icon
            src={ChevronDown}
            size={{ width: 24, height: 24 }}
            className={`transition-transform duration-150 ${
              isEventTypeOpen ? 'rotate-180' : 'rotate-0'
            }`}
          />
        </button>

        {isEventTypeOpen && (
          <div className='absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-grey2 z-50 min-w-[200px] py-1'>
            {/* All events */}
            <div className='flex items-center gap-2 px-4 py-2 hover:bg-grey1'>
              <CheckBox
                label={t('charger_history_all_events')}
                selected={isAllSelected}
                indeterminate={someAllSelected}
                isDisabled={false}
                onChange={handleToggleAll}
              />
            </div>

            {/* Separator */}
            <div className='mx-2 my-1 border-t border-grey3' />

            {/* Notes parent */}
            <div className='flex items-center justify-between px-4 py-2 hover:bg-grey1'>
              <div className='flex-1'>
                <CheckBox
                  label={t('charger_history_filter_notes')}
                  selected={allNotesSelected}
                  indeterminate={someNotesSelected}
                  onChange={handleToggleNotesAll}
                />
              </div>
              <div className='cursor-pointer p-1' onClick={toggleNotesExpanded}>
                <Icon
                  src={ChevronDown}
                  size={{ width: 24, height: 24 }}
                  className={`transition-transform duration-150 ${
                    isNotesExpanded ? 'rotate-180' : 'rotate-0'
                  }`}
                />
              </div>
            </div>

            {/* Charger child */}
            {isNotesExpanded && (
              <div className='flex items-center gap-2 pl-10 pr-4 py-1.5 hover:bg-grey1'>
                <CheckBox
                  label={t('charger_history_tag_charger')}
                  isDisabled={false}
                  selected={selectedTags.includes(SupportNoteTag.CHARGER)}
                  onChange={() => handleToggleTag(SupportNoteTag.CHARGER)}
                />
              </div>
            )}

            {/* Location child */}
            {isNotesExpanded && (
              <div className='flex items-center gap-2 pl-10 pr-4 py-1.5 hover:bg-grey1'>
                <CheckBox
                  label={t('charger_history_tag_location')}
                  isDisabled={false}
                  selected={selectedTags.includes(SupportNoteTag.LOCATION)}
                  onChange={() => handleToggleTag(SupportNoteTag.LOCATION)}
                />
              </div>
            )}

            {/* Model child */}
            {isNotesExpanded && (
              <div className='flex items-center gap-2 pl-10 pr-4 py-1.5 hover:bg-grey1'>
                <CheckBox
                  label={t('charger_history_tag_model')}
                  isDisabled={false}
                  selected={selectedTags.includes(SupportNoteTag.MODEL)}
                  onChange={() => handleToggleTag(SupportNoteTag.MODEL)}
                />
              </div>
            )}

            {/* Maintenance */}
            <div className='flex items-center gap-2 px-4 py-2 hover:bg-grey1'>
              <CheckBox
                label={t('charger_history_maintenance')}
                isDisabled={false}
                selected={selectedTags.includes(SupportNoteSource.MAINTENANCE)}
                onChange={() => handleToggleTag(SupportNoteSource.MAINTENANCE)}
              />
            </div>

            {/* Jira */}
            <div className='flex items-center gap-2 px-4 py-2 hover:bg-grey1'>
              <CheckBox
                label={t('charger_history_jira')}
                isDisabled={false}
                selected={selectedTags.includes(SupportNoteSource.JIRA)}
                onChange={() => handleToggleTag(SupportNoteSource.JIRA)}
              />
            </div>

            {/* Zendesk */}
            <div className='flex items-center gap-2 px-4 py-2 hover:bg-grey1'>
              <CheckBox
                label={t('charger_history_zendesk')}
                isDisabled={false}
                selected={selectedTags.includes(SupportNoteSource.ZENDESK)}
                onChange={() => handleToggleTag(SupportNoteSource.ZENDESK)}
              />
            </div>

            {/* Label (Spark-generated device messages) */}
            <div className='flex items-center gap-2 px-4 py-2 hover:bg-grey1'>
              <CheckBox
                label={t('charger_history_label')}
                isDisabled={false}
                selected={selectedTags.includes(SupportNoteSource.LABEL)}
                onChange={() => handleToggleTag(SupportNoteSource.LABEL)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChargerHistoryFilters;
